import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { addStudent } from "../services/studentService.js";
import { getAllClassrooms } from "../services/classroomService.js";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/StudentSubscriptionForm.css";

const StudentSubscriptionForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectTo = location.state?.redirectTo || "/";
  const intent = location.state?.intent || null;
  const inviteId = location.state?.inviteId || null;
  const invitedClassroomId = location.state?.classroomId || "";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    enrollmentNumber: "",
    classroomId: invitedClassroomId,
  });
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const fetchClassrooms = async () => {
      try {
        const classroomsData = await getAllClassrooms();
        setClassrooms(classroomsData);
      } catch (error) {
        console.error("Error fetching classrooms:", error);
        setError(t("studentSubscription.errors.classroomsFetchError"));
      }
    };
    fetchClassrooms();
  }, [t]);

  useEffect(() => {
    if (!invitedClassroomId) {
      return;
    }

    setFormData((prev) => {
      if (prev.classroomId) {
        return prev;
      }

      return { ...prev, classroomId: invitedClassroomId };
    });
  }, [invitedClassroomId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Apply phone mask dynamically
    if (name === "phone") {
      const digits = value.replace(/\D/g, "");
      let masked = "";

      if (digits.length <= 10) {
        // (XX) XXXX-XXXX
        masked = digits.replace(/^(\d{0,2})(\d{0,4})(\d{0,4}).*/, (m, d1, d2, d3) => {
          return [
            d1 ? `(${d1}` : "",
            d1.length === 2 ? ") " : "",
            d2,
            d3 ? `-${d3}` : "",
          ].join("");
        });
      } else {
        // (XX) XXXXX-XXXX
        masked = digits.replace(/^(\d{0,2})(\d{0,5})(\d{0,4}).*/, (m, d1, d2, d3) => {
          return [
            d1 ? `(${d1}` : "",
            d1.length === 2 ? ") " : "",
            d2,
            d3 ? `-${d3}` : "",
          ].join("");
        });
      }

      setFormData((prev) => ({ ...prev, phone: masked }));
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
  };

  const validateForm = () => {
    setError("");
    if (!formData.name || !formData.email || !formData.password) {
      setError(t("studentSubscription.errors.requiredFields"));
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t("studentSubscription.errors.invalidEmail"));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t("studentSubscription.errors.passwordMismatch"));
      return false;
    }
    if (formData.password.length < 6) {
      setError(t("studentSubscription.errors.weakPassword"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      await addStudent({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        enrollmentNumber: formData.enrollmentNumber || null,
        classroomId: formData.classroomId || null,
      });

      setSuccess(true);

      // Após cadastro, envia de volta ao login preservando redirect/intent
      setTimeout(() => {
        navigate("/login", {
          state: {
            redirectTo,
            intent,
            inviteId,
            classroomId: formData.classroomId || invitedClassroomId || null,
          },
          replace: true,
        });
      }, 800);
    } catch (error) {
      console.error("Error adding student:", error);
      setError(error.message || t("studentSubscription.errors.generalError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="student-subscription-container">
      <h2>{t("studentSubscription.title")}</h2>

      {success && (
        <div className="success-message">
          {t("studentSubscription.successMessage")}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="student-subscription-form">
        <div className="form-group">
          <label htmlFor="name">{t("studentSubscription.form.name")} *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">{t("studentSubscription.form.email")} *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone">{t("studentSubscription.form.phone")}</label>
          <input
            type="text"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            maxLength={15} // (99) 99999-9999
            placeholder="(99) 99999-9999"
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">{t("studentSubscription.form.password")} *</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">
            {t("studentSubscription.form.confirmPassword")} *
          </label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="classroomId">{t("studentSubscription.form.classroom")}</label>
          <select
            id="classroomId"
            name="classroomId"
            value={formData.classroomId}
            onChange={handleChange}
          >
            <option value="">
              {t("studentSubscription.form.defaultClassroom")}
            </option>
            {classrooms.map((classroom) => (
              <option key={classroom.id} value={classroom.id}>
                {classroom.name}
              </option>
            ))}
          </select>
          <small className="form-text">
            {t("studentSubscription.form.classroomHelp")}
          </small>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading
            ? t("studentSubscription.form.submitting")
            : t("studentSubscription.form.submit")}
        </button>
      </form>
    </div>
  );
};

export default StudentSubscriptionForm;
